"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { motion } from "motion/react";
import {
  ChevronDown,
  Globe,
  HelpCircle,
  LifeBuoy,
  ListTodo,
  LogOut,
  MapPin,
  Search,
  Sparkles,
  User,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { createClient } from "@/lib/supabase-browser";
import { ThemeToggle } from "./theme-toggle";
import { NotificationBell } from "./notification-bell";
import { PushManager } from "./push-manager";
import { Tooltip } from "./ui/tooltip";
import { replayTour } from "./onboarding-wizard";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
  DropdownLabel,
} from "./ui/dropdown-menu";
import type { User as SupaUser } from "@supabase/supabase-js";
import type { Tier } from "@/lib/tiers";

function getInitial(email: string | undefined): string {
  if (!email) return "?";
  return email[0].toUpperCase();
}

const AUTH_HINT_KEY = "rekkd:auth-hint";

/**
 * Optimistically render the logged-in chrome (beta banner, avatar) on reload
 * if the user was authed last time. Avoids the full-page CLS shift that
 * happens when auth resolves async and the banner pops in above everything.
 * Paired with an inline script in `layout.tsx` that sets `data-auth-hint`
 * on `<html>` before React hydrates; this function syncs the attribute
 * after auth resolves.
 */
function writeAuthHint(authed: boolean) {
  if (typeof window === "undefined") return;
  try {
    if (authed) {
      window.localStorage.setItem(AUTH_HINT_KEY, "1");
      document.documentElement.setAttribute("data-auth-hint", "1");
    } else {
      window.localStorage.removeItem(AUTH_HINT_KEY);
      document.documentElement.removeAttribute("data-auth-hint");
    }
  } catch { /* ignore */ }
}

export default function NavBar() {
  const [user, setUser] = useState<SupaUser | null>(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [quota, setQuota] = useState<{
    tier: Tier;
    used: number;
    limit: number;
    resetsAt?: string;
  } | null>(null);
  const router = useRouter();
  const pathname = usePathname();

  const isActive = (href: string) =>
    pathname === href || pathname?.startsWith(`${href}/`);

  const navPillClass = (href: string) =>
    cn(
      "hidden items-center rounded-[var(--radius-md)] px-[var(--space-3)] py-[var(--space-1-5)]",
      "text-[length:var(--font-size-sm)] font-[number:var(--font-weight-medium)]",
      "transition-colors sm:inline-flex",
      isActive(href)
        ? "bg-[var(--color-primary-subtle)] text-[var(--color-primary)]"
        : "text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]",
    );

  useEffect(() => {
    const supabase = createClient();
    let isActive = true;

    const fetchQuota = () => {
      fetch("/api/account")
        .then((r) => r.json())
        .then((d) => {
          if (isActive)
            setQuota({
              tier: d.tier,
              used: d.checksUsed,
              limit: d.checksLimit,
              resetsAt: d.resetsAt,
            });
        })
        .catch(() => {});
    };

    supabase.auth
      .getUser()
      .then(({ data }) => {
        if (!isActive) return;
        setUser(data.user);
        setAuthResolved(true);
        writeAuthHint(!!data.user);
        if (data.user) fetchQuota();
      })
      .catch(() => { if (isActive) setAuthResolved(true); });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      writeAuthHint(!!session?.user);
      if (session?.user) fetchQuota();
    });

    // Re-fetch quota when a check completes so the counter stays current
    const onCheckComplete = () => fetchQuota();
    window.addEventListener("rekkd:check-complete", onCheckComplete);

    // Re-check auth when tab becomes visible — catches session expiry,
    // logout in another tab, or login on another page.
    const onVisibility = () => {
      if (document.visibilityState !== "visible" || !isActive) return;
      supabase.auth.getUser().then(({ data }) => {
        if (!isActive) return;
        const current = data.user;
        setUser(current);
        writeAuthHint(!!current);
        if (current) fetchQuota();
      }).catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      isActive = false;
      subscription.unsubscribe();
      window.removeEventListener("rekkd:check-complete", onCheckComplete);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const handleLogout = async () => {
    setMenuOpen(false);
    // Clear state immediately — don't wait for auth listener
    setUser(null);
    setQuota(null);
    writeAuthHint(false);
    const supabase = createClient();
    await supabase.auth.signOut();
    // Hard navigate to clear all cached client state
    window.location.href = "/auth/login";
  };

  const tierLabel = !quota
    ? null
    : quota.tier === "pro"
      ? "Pro"
      : quota.tier === "agency"
        ? "Agency"
        : "Free";
  const usageText = quota
    ? quota.limit === 999999
      ? `${quota.used} checks used`
      : `${quota.used} / ${quota.limit} checks`
    : null;

  return (
    <nav
      className="sticky top-0 z-[var(--z-sticky)] w-full border-b border-[var(--color-border-default)] bg-[var(--color-bg-page)]"
      aria-label="Main navigation"
    >
      {/*
        Always render the banner DOM. Visibility is driven by CSS based on
        the `data-auth-hint` attribute set on <html> by an inline script in
        layout.tsx (before hydration) and kept in sync by writeAuthHint.
        Once auth resolves, we force-hide if no user (`data-force-hide`).
      */}
      <div
        data-beta-banner
        data-force-hide={authResolved && !user ? "1" : undefined}
        className="w-full bg-[var(--color-primary)] text-[var(--color-primary-foreground)]"
      >
        <div className="mx-auto flex max-w-[var(--container-xl)] items-center justify-center gap-[var(--space-2)] px-[var(--space-4)] py-[var(--space-1)] text-center text-[length:var(--font-size-xs)]">
          <Badge variant="secondary" className="shrink-0 text-[length:var(--font-size-xs)] px-[var(--space-2)] py-0">
            Beta
          </Badge>
          <span>
            <span className="hidden sm:inline">You&apos;re using an early version of Rekkd. Spot something off?{" "}</span>
            <span className="sm:hidden">Early version —{" "}</span>
            <Link href="/contact" className="underline font-[number:var(--font-weight-semibold)] hover:opacity-80">
              Let us know
            </Link>
          </span>
        </div>
      </div>
      <div className="mx-auto flex h-[var(--header-height)] max-w-[var(--container-xl)] items-center justify-between gap-[var(--space-2)] px-[var(--space-4)] sm:gap-[var(--space-4)] lg:px-[var(--space-8)]">
        {/* Logo */}
        <Link href="/" className="flex shrink-0 items-center gap-[var(--space-2)]">
          <img
            src="/logo.png"
            alt="Rekkd"
            width={32}
            height={32}
            className="size-8 shrink-0"
          />
          <span className="text-[length:var(--font-size-xl)] sm:text-[length:var(--font-size-2xl)] font-heading font-[number:var(--font-weight-bold)] tracking-[-0.05em] text-[var(--color-text-primary)]">
            Rekkd
          </span>
        </Link>

        {/* Right side */}
        <div className="flex shrink-0 items-center gap-[var(--space-1)] sm:gap-[var(--space-3)]">
          <ThemeToggle />

          {user && (
            <>
              <PushManager />
              <NotificationBell />
            </>
          )}

          {!user && (
            <>
              <Link
                href="/explore"
                className="hidden text-[length:var(--font-size-sm)] font-[number:var(--font-weight-medium)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors sm:inline-flex"
              >
                Explore
              </Link>
              <Button
                render={<Link href="/auth/login" />}
                variant="ghost"
                size="sm"
              >
                Log in
              </Button>
              <Button
                render={<Link href="/auth/signup" />}
                variant="primary"
                size="sm"
              >
                Sign Up Free
              </Button>
            </>
          )}

          {user ? (
            <>
              <Link
                href="/places"
                data-tour="nav-places"
                className={navPillClass("/places")}
              >
                My Places
              </Link>
              <Link
                href="/tasks"
                data-tour="nav-tasks"
                className={navPillClass("/tasks")}
              >
                Tasks
              </Link>
              <Link
                href="/insights"
                className={navPillClass("/insights")}
              >
                Insights
              </Link>
              <Link href="/explore" className={navPillClass("/explore")}>
                Explore
              </Link>

              {quota && quota.limit !== 999999 && (() => {
                const resetsLabel = quota.resetsAt
                  ? new Date(quota.resetsAt).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })
                  : null;
                // Tooltip copy — the `Tooltip` component renders this
                // with themed typography + an arrow. Replaces the old
                // native `title` + `aria-label` combo whose styling
                // varied across browsers.
                const tooltip = resetsLabel
                  ? `${quota.used} of ${quota.limit} checks used this month. Resets ${resetsLabel}.`
                  : `${quota.used} of ${quota.limit} checks used this month.`;
                return (
                  <Tooltip
                    label={tooltip}
                    side="bottom"
                    className="hidden sm:inline-flex"
                  >
                    {/* No explicit cursor — Tooltip's default (no
                        class) matches the themed tooltip behaviour on
                        theme-toggle / pin-button / re-check button
                        that sit alongside this pill in the nav. */}
                    <span
                      className="inline-flex items-center gap-[var(--space-2)] pl-[var(--space-2)]"
                      role="status"
                    >
                      <span className="h-[5px] w-[60px] overflow-hidden rounded-[var(--radius-full)] bg-[var(--color-bg-muted)]">
                        {/*
                          Quota fill: spring on width so the bar bounces
                          to its new value when a check completes (the
                          `rekkd:check-complete` listener bumps `used`).
                          Initial width is the same as animate so we
                          don't see a 0 → fill swing on every page load.
                        */}
                        <motion.span
                          className="block h-full bg-[var(--color-primary)]"
                          initial={false}
                          animate={{
                            width: `${Math.min(100, Math.max(0, (quota.used / quota.limit) * 100))}%`,
                          }}
                          transition={{ type: "spring", stiffness: 200, damping: 26 }}
                        />
                      </span>
                      <span className="font-mono text-[length:var(--font-size-xs)] text-[var(--color-text-muted)]">
                        {quota.used} / {quota.limit}
                      </span>
                    </span>
                  </Tooltip>
                );
              })()}

              {/* Avatar dropdown */}
              <DropdownMenu>
                <DropdownTrigger
                  open={menuOpen}
                  onClick={() => setMenuOpen(!menuOpen)}
                  data-tour="nav-avatar"
                  className="flex shrink-0 items-center gap-[var(--space-2)] rounded-[var(--radius-full)] border border-[var(--color-border-default)] bg-[var(--color-bg-card)] px-[var(--space-1)] py-[var(--space-1)] transition-colors hover:bg-[var(--color-bg-muted)] sm:px-[var(--space-3)] sm:py-[var(--space-2)]"
                >
                  {user.user_metadata?.avatar_url ? (
                    <img
                      src={user.user_metadata.avatar_url}
                      alt=""
                      className="size-8 rounded-[var(--radius-full)] object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="flex size-8 items-center justify-center rounded-[var(--radius-full)] bg-[var(--color-primary)] text-[length:var(--font-size-xs)] font-[number:var(--font-weight-semibold)] text-[var(--color-primary-foreground)]">
                      {getInitial(user.email)}
                    </div>
                  )}
                  {quota && (
                    <span className="hidden text-[length:var(--font-size-xs)] text-[var(--color-text-muted)] sm:inline">
                      {tierLabel}
                    </span>
                  )}
                  <ChevronDown
                    className="size-3 text-[var(--color-text-muted)]"
                    aria-hidden="true"
                  />
                </DropdownTrigger>

                <DropdownContent
                  open={menuOpen}
                  onClose={() => setMenuOpen(false)}
                  className="w-72"
                >
                  {/* User info */}
                  <div className="border-b border-[var(--color-border-subtle)] px-[var(--space-3)] py-[var(--space-3)]">
                    <p className="truncate text-[length:var(--font-size-sm)] font-[number:var(--font-weight-medium)] text-[var(--color-text-primary)]">
                      {user.email}
                    </p>
                    <div className="mt-[var(--space-1)] flex items-center gap-[var(--space-2)]">
                      {tierLabel && <Badge variant="default">{tierLabel}</Badge>}
                      {usageText && (
                        <span className="text-[length:var(--font-size-xs)] text-[var(--color-text-muted)]">
                          {usageText}
                        </span>
                      )}
                    </div>
                  </div>

                  {/*
                    Menu order is tuned to usage frequency, grouped with
                    separators so the most-reached-for actions float at the
                    top. See #456.
                    1. Run check                      ← primary action
                    2. My Places / Tasks / Insights / Account   ← daily surfaces
                    3. Explore / Help / App Tour      ← occasional support
                    4. Sign out                       ← final/destructive

                    What's New was retired from this menu (it's still in the
                    footer); Notifications were never a top-level menu item.
                  */}
                  <Link href="/new" onClick={() => setMenuOpen(false)}>
                    <DropdownItem>
                      <Search className="size-4" aria-hidden="true" />
                      Run check
                    </DropdownItem>
                  </Link>

                  <DropdownSeparator />

                  <Link
                    href="/places"
                    onClick={() => setMenuOpen(false)}
                    className="sm:hidden"
                  >
                    <DropdownItem>
                      <MapPin className="size-4" aria-hidden="true" />
                      My Places
                    </DropdownItem>
                  </Link>
                  <Link href="/tasks" onClick={() => setMenuOpen(false)}>
                    <DropdownItem>
                      <ListTodo className="size-4" aria-hidden="true" />
                      Tasks
                    </DropdownItem>
                  </Link>
                  <Link href="/insights" onClick={() => setMenuOpen(false)}>
                    <DropdownItem>
                      <Sparkles className="size-4" aria-hidden="true" />
                      Insights
                    </DropdownItem>
                  </Link>
                  <Link href="/account" onClick={() => setMenuOpen(false)}>
                    <DropdownItem>
                      <User className="size-4" aria-hidden="true" />
                      Account
                    </DropdownItem>
                  </Link>

                  <DropdownSeparator />

                  <Link href="/explore" onClick={() => setMenuOpen(false)}>
                    <DropdownItem>
                      <Globe className="size-4" aria-hidden="true" />
                      Explore Cities
                    </DropdownItem>
                  </Link>
                  <Link href="/contact" onClick={() => setMenuOpen(false)}>
                    <DropdownItem>
                      <LifeBuoy className="size-4" aria-hidden="true" />
                      Help
                    </DropdownItem>
                  </Link>
                  <DropdownItem
                    onClick={() => {
                      setMenuOpen(false);
                      if (pathname === "/") {
                        // Already on the dashboard — replay via event so the
                        // wizard restarts without a navigation no-op.
                        replayTour();
                      } else {
                        try {
                          sessionStorage.setItem("rekkd-replay-tour", "1");
                        } catch {
                          // sessionStorage unavailable
                        }
                        router.push("/");
                      }
                    }}
                  >
                    <HelpCircle className="size-4" aria-hidden="true" />
                    App Tour
                  </DropdownItem>

                  <DropdownSeparator />

                  <DropdownItem onClick={handleLogout} destructive>
                    <LogOut className="size-4" aria-hidden="true" />
                    Sign out
                  </DropdownItem>
                </DropdownContent>
              </DropdownMenu>
            </>
          ) : null}
        </div>
      </div>
    </nav>
  );
}
